import { ScheduleOutlined, TableOutlined } from "@ant-design/icons"
import { ConfigProvider, Flex, Segmented, Tabs, Typography } from "antd"
import { useMemo, useState } from "react"

import type { ProjectMilestoneFilters } from "../../../../../../redux/project.types.ts"
import type {
  Project,
  ProjectTypeDetail,
  ProjectTypeFormData
} from "../../../../../../types/project.types.ts"
import { instanceKeyOf } from "../../../../../../helpers/repeated-project-type.ts"
import { ProjectViewAddProjectTypeController } from "../../ProjectViewAddProjectTypeModal/ProjectViewAddProjectTypeController.tsx"
import { ProjectViewAddProjectTypeModal } from "../../ProjectViewAddProjectTypeModal/ProjectViewAddProjectTypeModal.tsx"
import { ProjectViewMilestonesHeaderItem } from "./ProjectViewMilestonesHeaderItem.tsx"
import {
  TabDragHandle,
  useSortableTabBar
} from "./ProjectViewMilestonesSortableTabBar.tsx"

interface ProjectViewMilestonesHeaderProps {
  project: Project
  isLocked: boolean
  /** The open tab's key, derived the same way every tab's is. */
  activeTabKey: string
  //
  projectEditAllowed?: boolean
  editAllowed?: boolean
  saveAsTemplateAllowed?: boolean
  //
  view: "table" | "gantt"
  onViewChange: (view: "table" | "gantt") => void
  //
  onFilterChange: (data: Partial<ProjectMilestoneFilters>) => void
  onAddProjectType: (data: ProjectTypeFormData) => Promise<boolean | void>
  onUpdateProjectType: (
    data: ProjectTypeFormData,
    previous: ProjectTypeDetail
  ) => Promise<void>
  onDeleteProjectType: (item: ProjectTypeDetail) => Promise<void>
  onReorderProjectTypes: (orderedIds: string[]) => Promise<void>
  onSaveProjectTypeAsTemplate: (item: ProjectTypeDetail) => void
}

export function ProjectViewMilestonesHeader({
  project,
  isLocked,
  activeTabKey,
  projectEditAllowed,
  editAllowed,
  saveAsTemplateAllowed,
  view,
  onViewChange,
  onFilterChange,
  onAddProjectType,
  onUpdateProjectType,
  onDeleteProjectType,
  onReorderProjectTypes,
  onSaveProjectTypeAsTemplate
}: ProjectViewMilestonesHeaderProps) {
  const [editable, setEditable] = useState<ProjectTypeDetail | null>(null)

  const projectTypeDetails = project?.project_type_details

  const onUpdateProjectTypeHandler = (data: ProjectTypeFormData) => {
    if (!editable?.id) return
    return onUpdateProjectType(
      {
        ...data,
        project_type_id: editable.id,
        assignment_id: editable.assignment_id
      },
      editable
    )
  }

  const reorderTabsAllowed =
    !!projectEditAllowed && !isLocked && (projectTypeDetails?.length || 0) > 1

  const projectTypes = useMemo(
    () =>
      projectTypeDetails?.map((p) => {
        return {
          key: instanceKeyOf(p),
          disabled: isLocked,
          className: "bg-red-500",
          label: (
            <Flex align={"center"} gap={4}>
              <TabDragHandle visible={reorderTabsAllowed} />
              <ProjectViewMilestonesHeaderItem
                item={p}
                isLocked={isLocked}
                editAllowed={projectEditAllowed}
                onEdit={setEditable}
                deleteAllowed={projectEditAllowed}
                onDelete={onDeleteProjectType}
                saveAsTemplateAllowed={saveAsTemplateAllowed}
                onSaveAsTemplate={onSaveProjectTypeAsTemplate}
              />
            </Flex>
          )
        }
      }),
    [
      projectTypeDetails,
      editAllowed,
      isLocked,
      onDeleteProjectType,
      projectEditAllowed,
      reorderTabsAllowed,
      saveAsTemplateAllowed,
      onSaveProjectTypeAsTemplate
    ]
  )

  const renderTabBar = useSortableTabBar({
    items: projectTypes || [],
    reorderAllowed: reorderTabsAllowed,
    onReorder: (orderedIds) => void onReorderProjectTypes(orderedIds)
  })

  return (
    <Flex gap="small" vertical>
      {editable && (
        <ProjectViewAddProjectTypeModal
          open
          isEdit
          initialValues={{
            project_type_id: editable.id,
            assignment_id: editable.assignment_id,
            category: editable.category,
            estimated_receivable: Number(editable.estimated_receivable ?? 0),
            estimated_payable: Number(editable.estimated_payable ?? 0)
          }}
          onClose={() => setEditable(null)}
          onSubmit={onUpdateProjectTypeHandler}
        />
      )}

      <Typography.Title level={4} style={{ marginBottom: 0 }}>
        Project Milestones
      </Typography.Title>

      <ConfigProvider
        theme={{
          components: {
            Segmented: {
              controlHeight: 38,
              trackBg: "#f2f4f6",
              itemColor: "#5b6572",
              itemHoverColor: "var(--ant-color-primary)",
              itemSelectedBg: "var(--ant-color-primary)",
              itemSelectedColor: "#fff"
            }
          }
        }}
      >
        <Segmented
          value={view}
          onChange={(value) => onViewChange(value as "table" | "gantt")}
          options={[
            {
              value: "table",
              label: (
                <Flex align="center" gap={6} style={{ padding: "0 6px" }}>
                  <TableOutlined style={{ fontSize: 14 }} />
                  <span>Table</span>
                </Flex>
              )
            },
            {
              value: "gantt",
              label: (
                <Flex align="center" gap={6} style={{ padding: "0 6px" }}>
                  <ScheduleOutlined style={{ fontSize: 14 }} />
                  <span>Gantt</span>
                </Flex>
              )
            }
          ]}
          style={{
            alignSelf: "flex-start",
            border: "1px solid #e5e8eb",
            borderRadius: 8,
            padding: 2
          }}
        />
      </ConfigProvider>

      <Tabs
        className={"[&_.ant-tabs-tab]:p-0"}
        activeKey={activeTabKey}
        tabBarExtraContent={
          projectEditAllowed ? (
            <ProjectViewAddProjectTypeController
              onAddProjectType={onAddProjectType}
            />
          ) : undefined
        }
        onChange={(key) => {
          const opened = projectTypeDetails?.find((p) => instanceKeyOf(p) === key)
          if (!opened) return
          onFilterChange({
            project_type: opened.id,
            project_type_assignment: opened.assignment_id ?? null
          })
        }}
        type="card"
        items={projectTypes}
        renderTabBar={renderTabBar}
      />
    </Flex>
  )
}
